import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router';
import { Spin } from '@arco-design/web-react';
import { RequireAuth } from './RequireAuth';
import { AuthLayout } from '../layouts/AuthLayout';
import { MainLayout } from '../layouts/MainLayout';

// 路由级懒加载
const LoginPage = lazy(() => import('../pages/login').then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazy(() =>
  import('../pages/register').then((m) => ({ default: m.RegisterPage })),
);
const DashboardPage = lazy(() =>
  import('../pages/dashboard').then((m) => ({ default: m.DashboardPage })),
);
const AppListPage = lazy(() =>
  import('../pages/apps/list').then((m) => ({ default: m.AppListPage })),
);
const AppCreatePage = lazy(() =>
  import('../pages/apps/create').then((m) => ({ default: m.AppCreatePage })),
);
const AppEditPage = lazy(() =>
  import('../pages/apps/edit').then((m) => ({ default: m.AppEditPage })),
);
const AppDetailPage = lazy(() =>
  import('../pages/apps/detail').then((m) => ({ default: m.AppDetailPage })),
);
const CardTypesPage = lazy(() =>
  import('../pages/apps/card-types').then((m) => ({ default: m.CardTypesPage })),
);
const CardTypeEditPage = lazy(() =>
  import('../pages/apps/card-types/edit').then((m) => ({ default: m.CardTypeEditPage })),
);
const CardsPage = lazy(() => import('../pages/apps/cards').then((m) => ({ default: m.CardsPage })));
const CardUsagesPage = lazy(() =>
  import('../pages/card-usages').then((m) => ({ default: m.CardUsagesPage })),
);
const CredentialsPage = lazy(() =>
  import('../pages/credentials').then((m) => ({ default: m.CredentialsPage })),
);
const AgentsPage = lazy(() => import('../pages/agents').then((m) => ({ default: m.AgentsPage })));
const AgentPurchasesPage = lazy(() =>
  import('../pages/agent-purchases').then((m) => ({ default: m.AgentPurchasesPage })),
);
const RemoteVariablesPage = lazy(() =>
  import('../pages/remote-variables').then((m) => ({ default: m.RemoteVariablesPage })),
);
const RemoteVariableEditPage = lazy(() =>
  import('../pages/remote-variables/edit').then((m) => ({ default: m.RemoteVariableEditPage })),
);
const RemoteFunctionsPage = lazy(() =>
  import('../pages/remote-functions').then((m) => ({ default: m.RemoteFunctionsPage })),
);
const RemoteFunctionEditPage = lazy(() =>
  import('../pages/remote-functions/edit').then((m) => ({ default: m.RemoteFunctionEditPage })),
);
const FeatureQuotasPage = lazy(() =>
  import('../pages/feature-quotas').then((m) => ({ default: m.FeatureQuotasPage })),
);
const FeatureQuotaUsagesPage = lazy(() =>
  import('../pages/feature-quotas/usages').then((m) => ({ default: m.FeatureQuotaUsagesPage })),
);
const FeatureQuotaLogsPage = lazy(() =>
  import('../pages/feature-quota-logs').then((m) => ({ default: m.FeatureQuotaLogsPage })),
);
const TimedFeaturesPage = lazy(() =>
  import('../pages/timed-features').then((m) => ({ default: m.TimedFeaturesPage })),
);
const ForwardingFeaturesPage = lazy(() =>
  import('../pages/forwarding-features').then((m) => ({ default: m.ForwardingFeaturesPage })),
);
const ForwardingRecordsPage = lazy(() =>
  import('../pages/forwarding-records').then((m) => ({ default: m.ForwardingRecordsPage })),
);
const TimedFeatureGrantsPage = lazy(() =>
  import('../pages/timed-features/grants').then((m) => ({ default: m.TimedFeatureGrantsPage })),
);
const BlacklistPage = lazy(() =>
  import('../pages/blacklist').then((m) => ({ default: m.BlacklistPage })),
);
const EndUsersPage = lazy(() =>
  import('../pages/end-users').then((m) => ({ default: m.EndUsersPage })),
);
const OnlineUsersPage = lazy(() =>
  import('../pages/end-users/online').then((m) => ({ default: m.OnlineUsersPage })),
);
const AuditLogsPage = lazy(() =>
  import('../pages/audit-logs').then((m) => ({ default: m.AuditLogsPage })),
);
const ClientLogsPage = lazy(() =>
  import('../pages/client-logs').then((m) => ({ default: m.ClientLogsPage })),
);
const PaymentOrdersPage = lazy(() =>
  import('../pages/payment-orders').then((m) => ({ default: m.PaymentOrdersPage })),
);
const TicketsPage = lazy(() =>
  import('../pages/tickets').then((m) => ({ default: m.TicketsPage })),
);
const TicketDetailPage = lazy(() =>
  import('../pages/tickets/detail').then((m) => ({ default: m.TicketDetailPage })),
);
const NotFoundPage = lazy(() =>
  import('../pages/not-found').then((m) => ({ default: m.NotFoundPage })),
);

// 页面加载中占位
function PageLoading() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 48 }}>
      <Spin size={32} />
    </div>
  );
}

export function AppRouter() {
  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        {/* 认证路由（无需登录） */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
        </Route>

        {/* 受保护路由（需登录） */}
        <Route
          element={
            <RequireAuth>
              <MainLayout />
            </RequireAuth>
          }
        >
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/apps" element={<AppListPage />} />
          <Route path="/apps/create" element={<AppCreatePage />} />
          <Route path="/apps/:id" element={<AppDetailPage />} />
          <Route path="/apps/:id/edit" element={<AppEditPage />} />
          <Route path="/credentials" element={<CredentialsPage />} />
          <Route path="/card-types" element={<CardTypesPage />} />
          <Route path="/card-types/:id/edit" element={<CardTypeEditPage />} />
          <Route path="/cards" element={<CardsPage />} />
          <Route path="/card-usages" element={<CardUsagesPage />} />
          <Route path="/agents" element={<AgentsPage />} />
          <Route path="/agent-purchases" element={<AgentPurchasesPage />} />
          <Route path="/remote-variables" element={<RemoteVariablesPage />} />
          <Route path="/remote-variables/:id/edit" element={<RemoteVariableEditPage />} />
          <Route path="/remote-functions" element={<RemoteFunctionsPage />} />
          <Route path="/remote-functions/:id/edit" element={<RemoteFunctionEditPage />} />
          <Route path="/feature-quotas" element={<FeatureQuotasPage />} />
          <Route path="/feature-quotas/:id/usages" element={<FeatureQuotaUsagesPage />} />
          <Route path="/feature-quota-logs" element={<FeatureQuotaLogsPage />} />
          <Route path="/timed-features" element={<TimedFeaturesPage />} />
          <Route path="/timed-features/:id/grants" element={<TimedFeatureGrantsPage />} />
          <Route path="/forwarding-features" element={<ForwardingFeaturesPage />} />
          <Route path="/forwarding-records" element={<ForwardingRecordsPage />} />
          <Route path="/blacklist" element={<BlacklistPage />} />
          <Route path="/end-users" element={<EndUsersPage />} />
          <Route path="/end-users/online" element={<OnlineUsersPage />} />
          <Route path="/audit-logs" element={<AuditLogsPage />} />
          <Route path="/client-logs" element={<ClientLogsPage />} />
          <Route path="/payment-orders" element={<PaymentOrdersPage />} />
          <Route path="/tickets" element={<TicketsPage />} />
          <Route path="/tickets/:ticketId" element={<TicketDetailPage />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
